/**
 * Styled-components implementation of the full ContentBlock.
 *
 * This is the legacy fallback the Hybrid delegates to whenever `contentProps`
 * carries styled-system props (e.g. `bg`, `py`) that a plain Tailwind element
 * cannot honor. It reproduces the original Box/Container/FooterContainer tree
 * verbatim so styled-system passthrough and visual parity are preserved.
 */

import React, { useState } from "react";
import { Box } from "@sproutsocial/seeds-react-box";
import { Loader } from "@sproutsocial/seeds-react-loader";
import { Collapsible } from "@sproutsocial/seeds-react-collapsible";
import {
  ContentHeader,
  CollapsibleContentHeader,
} from "@sproutsocial/seeds-react-content-header";
import { Container } from "@sproutsocial/seeds-react-container";
import { FooterContainer } from "./styles";
import type {
  ContentBlockBaseProps,
  ContentBlockProps,
  CollapsibleContentBlockProps,
} from "./ContentBlock";

const ContentArea = ({
  isLoading,
  children,
  contentProps,
  flushLayout,
  hasFooter,
}: Pick<
  ContentBlockBaseProps,
  "isLoading" | "children" | "contentProps" | "flushLayout"
> & { hasFooter?: boolean }) => (
  <Box
    p={flushLayout ? 0 : 400}
    {...contentProps}
    borderBottomLeftRadius={hasFooter ? undefined : "outer"}
    borderBottomRightRadius={hasFooter ? undefined : "outer"}
  >
    {isLoading ? (
      <Box my={400}>
        <Loader delay={false} />
      </Box>
    ) : (
      children
    )}
  </Box>
);

const ContentFooter = ({
  footerContent,
  footerActions,
}: {
  footerContent?: React.ReactNode;
  footerActions?: React.ReactNode;
}) => (
  <FooterContainer>
    {footerContent && <span>{footerContent}</span>}
    {footerActions && <span>{footerActions}</span>}
  </FooterContainer>
);

const CollapsibleContentBlock = ({
  title,
  subtitle,
  titleAs = "h2",
  subtitleAs = "h3",
  children,
  isLoading,
  headerActions,
  rightTitleElement,
  contentProps,
  flushLayout,
  footerContent,
  footerActions,
  isOpen: controlledIsOpen,
  defaultOpen = true,
  onOpenChange,
}: Omit<CollapsibleContentBlockProps, "isCollapsible">) => {
  const [internalOpen, setInternalOpen] = useState(defaultOpen);
  const open = controlledIsOpen !== undefined ? controlledIsOpen : internalOpen;
  const hasFooter = !!(footerContent || footerActions);

  const handleOpenChange = (newOpen: boolean) => {
    if (controlledIsOpen === undefined) {
      setInternalOpen(newOpen);
    }
    onOpenChange?.(newOpen);
  };

  return (
    <Collapsible isOpen={open} onOpenChange={handleOpenChange}>
      <Container as="section">
        <CollapsibleContentHeader
          title={title}
          subtitle={subtitle}
          headingLevel={titleAs}
          subtitleAs={subtitleAs}
          headerActions={headerActions}
          rightTitleElement={rightTitleElement}
          trigger={Collapsible.Trigger}
          triggerPosition="left"
        />
        <Collapsible.Panel>
          <ContentArea
            isLoading={isLoading}
            contentProps={contentProps}
            flushLayout={flushLayout}
            hasFooter={hasFooter}
          >
            {children}
          </ContentArea>
          {hasFooter && (
            <ContentFooter
              footerContent={footerContent}
              footerActions={footerActions}
            />
          )}
        </Collapsible.Panel>
      </Container>
    </Collapsible>
  );
};

export const ContentBlockStyled = ({
  isCollapsible,
  ...props
}: ContentBlockProps) => {
  if (isCollapsible) {
    return <CollapsibleContentBlock {...props} />;
  }

  const {
    title,
    subtitle,
    titleAs = "h2",
    subtitleAs = "h3",
    children,
    isLoading,
    headerActions,
    rightTitleElement,
    contentProps,
    flushLayout,
    footerContent,
    footerActions,
  } = props;

  const hasFooter = !!(footerContent || footerActions);

  return (
    <Container as="section">
      <ContentHeader
        title={title}
        subtitle={subtitle}
        headingLevel={titleAs}
        subtitleAs={subtitleAs}
        headerActions={headerActions}
        rightTitleElement={rightTitleElement}
      />
      <ContentArea
        isLoading={isLoading}
        contentProps={contentProps}
        flushLayout={flushLayout}
        hasFooter={hasFooter}
      >
        {children}
      </ContentArea>
      {hasFooter && (
        <ContentFooter
          footerContent={footerContent}
          footerActions={footerActions}
        />
      )}
    </Container>
  );
};
